import { ToolOutlined, UsergroupAddOutlined } from '@ant-design/icons';
import { landmineNames } from '@mm/constants/landmine-names';
import { getIsAutomationFeatureEnabled } from '@mm/state/selectors/automations';
import classNames from 'classnames';
import pluralize from 'pluralize';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import {
  ButtonColor,
  PrimaryButton,
} from '../../../common/buttons/PrimaryButton/PrimaryButton';
import {
  Countdown,
  ParsedTimeLeft,
} from '../../../common/components/Countdown/Countdown';
import { ReactComponent as StopwatchIcon } from './stopwatch.svg';
import { useFreeTrialUpgradeModal } from '../../../common/modals/FreeTrialUpgradeModal/use-free-trial-upgrade-modal';
import './FreeTrialCountdown.pcss';
import { useResolutionInfo } from '@mm/hooks';
import { MOBILE_SMALL } from '../../../editor/constants/screenSize';
import {
  selectIsShopifyContext,
  selectShopifyShop,
} from '@mm/state/selectors/viewSelectors';

export const FreeTrialCountdown = () => {
  const isShopify = useSelector(selectIsShopifyContext);
  const shop = useSelector(selectShopifyShop);
  const {
    isFreeTrialPromotionActive,
    freeTrialPromotion,
    currentSubscription,
    allowedToManageBilling,
  } = useFreeTrialUpgradeModal();
  const { screenWidth } = useResolutionInfo();
  const isMobile = screenWidth <= MOBILE_SMALL;
  const isAutomationsEnabled = useSelector(getIsAutomationFeatureEnabled);
  const [parsedTimeLeft, setParsedTimeLeft] = useState<ParsedTimeLeft>();
  const { openFreeTrialUpgradeModalPromotional } = useFreeTrialUpgradeModal();

  if (!freeTrialPromotion?.expiresAt) return null;

  if (isShopify && !isFreeTrialPromotionActive) {
    return <></>;
  }

  const label = parsedTimeLeft?.days
    ? `${parsedTimeLeft?.days} ${pluralize('day', parsedTimeLeft?.days)}`
    : `${parsedTimeLeft?.hours}:${parsedTimeLeft?.minutes}`;

  const showAsAlert = parsedTimeLeft && parsedTimeLeft?.days <= 2;

  const freeTrialCountdown = (
    <div className="free-trial-countdown__label">
      <StopwatchIcon />
      {label} of Free Trial left
    </div>
  );

  const leadCount = isShopify ? shop?.contactsLimits || 500 : 500;

  return (
    <div
      className={classNames('free-trial-countdown', {
        'free-trial-countdown__with-automations': isAutomationsEnabled,
        'free-trial-countdown__green': isShopify,
        'free-trial-countdown__red-alert': showAsAlert,
        'free-trial-countdown__invisible': !parsedTimeLeft,
      })}
    >
      <div className="free-trial-countdown__countdown-container">
        <Countdown
          expiresAt={freeTrialPromotion.expiresAt}
          parsedTimeLeft={parsedTimeLeft}
          setParsedTimeLeft={setParsedTimeLeft}
          withSeconds={!isAutomationsEnabled}
          customRenderer={freeTrialCountdown}
          css={
            isAutomationsEnabled
              ? {
                  countdownDays: 'free-trial-countdown__days',
                  countdownDaysText: 'free-trial-countdown__days-text',
                  countdownHoursAndMinutes: 'free-trial-countdown__time',
                }
              : {}
          }
        />
        {allowedToManageBilling &&
          !(currentSubscription && Object.keys(currentSubscription).length) && (
            <PrimaryButton
              color={ButtonColor.transparent}
              onClick={() =>
                openFreeTrialUpgradeModalPromotional({
                  landmineName: landmineNames.FREE_TRIAL_EXTEND_BUTTON,
                })
              }
              className={classNames({
                'free-trial-countdown__button': isAutomationsEnabled,
                'free-trial-countdown__button-alert': showAsAlert,
              })}
            >
              Choose Plan
            </PrimaryButton>
          )}
      </div>
      {!isMobile && (
        <>
          {' '}
          <div className="free-trial-countdown__benefit">
            <ToolOutlined />
            Unlimited tools.
          </div>
          <div className="free-trial-countdown__benefit">
            <UsergroupAddOutlined />
            Up to {leadCount} {pluralize('lead', leadCount)}.
          </div>
        </>
      )}
    </div>
  );
};
